import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { createTeacherSupportRequest } from "../services/TeacherAPIClient";
import {
  buildTeacherSupportRequestPayload,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherTrialAccessStatusModal.css";

const PURCHASE_AFTER_TRIAL_ISSUE = "We want to keep using ALPACA after the trial";
const EXTEND_TRIAL_ISSUE = "Our trial says expired";

const TRIAL_ISSUES = [
  {
    value: "Start our trial",
    detail: "We want to begin using ALPACA"
  },
  {
    value: EXTEND_TRIAL_ISSUE,
    detail: "We did not get enough time to use it"
  },
  {
    value: "Screeners are blocked",
    detail: "Trial access is stopping setup"
  },
  {
    value: PURCHASE_AFTER_TRIAL_ISSUE,
    detail: "Get purchase and invoice details"
  }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const findMatchingOption = (options, value) => {
  const normalisedValue = normaliseComparableText(value);

  if (!normalisedValue) {
    return "";
  }

  return (
    options.find((option) => {
      const optionValue = normaliseComparableText(option.value);
      return optionValue === normalisedValue || optionValue.replace(/\s+/g, "-") === normalisedValue;
    })?.value || ""
  );
};

const normalisePrefill = (prefill) => ({
  trialIssue: findMatchingOption(
    TRIAL_ISSUES,
    prefill?.trialIssue || prefill?.trial_issue || prefill?.issue || prefill?.trialStatus
  ),
  note: normaliseText(prefill?.note),
  schoolName: normaliseText(prefill?.schoolName || prefill?.school_name)
});

function TeacherTrialAccessStatusModal({
  show,
  handleClose,
  prefill = null,
  schoolName = "",
  schoolid = null,
  onOpenGuidance = null,
  onOpenPurchaseDetails = null,
  onOpenExtendTrial = null,
  onOpenSupport = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const [stage, setStage] = useState("issue");
  const [trialIssue, setTrialIssue] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);

  const displayedSchoolName = prefillContext.schoolName || normaliseText(schoolName) || "your school";
  const trimmedNote = normaliseText(note);
  const wantsPurchaseFromIssue = trialIssue === PURCHASE_AFTER_TRIAL_ISSUE;
  const wantsExtendFromIssue = trialIssue === EXTEND_TRIAL_ISSUE;
  const canReview = Boolean(trialIssue && (trimmedNote || wantsPurchaseFromIssue || wantsExtendFromIssue));

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("issue");
    setTrialIssue(prefillContext.trialIssue);
    setNote(prefillContext.note);
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
  }, [show, prefillContext]);

  const wantsPurchaseDetails = wantsPurchaseFromIssue;
  const returnContext = {
    returnWorkflow: "trial-access-status",
    returnTrialIssue: trialIssue,
    returnTrialNote: note
  };

  const openPurchaseDetails = () => {
    if (onOpenPurchaseDetails) {
      onOpenPurchaseDetails({
        invoiceRequestType: "Question about pricing",
        ...returnContext,
        note: trimmedNote
          ? `We want to continue after using the trial. ${trimmedNote}`
          : "We want to continue after using the trial."
      });
      return;
    }

    openTrialGuidance();
  };

  const openExtendTrial = () => {
    if (onOpenExtendTrial) {
      onOpenExtendTrial({
        reason: "We have not had time to screen yet",
        ...returnContext,
        note: trimmedNote || "Our trial says expired."
      });
      return;
    }

    handleClose();
  };

  const openTrialGuidance = () => {
    if (onOpenGuidance) {
      onOpenGuidance({
        guidanceQuery: wantsPurchaseDetails ? "purchase invoice trial" : "trial access"
      });
      return;
    }

    handleClose();
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Trial access request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const returnToTeacherDashboard = () => {
    if (embedded) {
      handleDone();
      return;
    }

    if (onReturnToTeacherDashboard) {
      onReturnToTeacherDashboard();
      return;
    }

    handleClose();
  };

  const handleNext = () => {
    if (!trialIssue) {
      setError("Choose the trial issue.");
      return;
    }

    if (wantsPurchaseFromIssue) {
      openPurchaseDetails();
      return;
    }

    if (wantsExtendFromIssue) {
      openExtendTrial();
      return;
    }

    if (!trimmedNote) {
      setError("Add a short note about what happened.");
      return;
    }

    if (onOpenSupport) {
      onOpenSupport({
        issueType: trialIssue === "Screeners are blocked" ? "Screener will not load" : "Other issue",
        description: trimmedNote,
        ...returnContext
      });
      return;
    }

    sendTrialAccessRequest();
  };

  const sendTrialAccessRequest = async () => {
    if (!canReview || wantsPurchaseFromIssue || wantsExtendFromIssue) {
      return;
    }

    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const payload = buildTeacherSupportRequestPayload({
      type: "trial_extension",
      schoolId: schoolid,
      details: {
        trialIssue,
        note: trimmedNote,
        schoolName: displayedSchoolName
      },
      source: {
        workflow: "trial-access-status"
      }
    });

    try {
      const response = await createTeacherSupportRequest(payload);
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderOption = (option, selectedValue, setValue, className, name) => {
    const selected = selectedValue === option.value;

    return (
      <button
        key={option.value}
        type="button"
        className={`${className}${selected ? " selected" : ""}`}
        onClick={() => {
          setValue(option.value);
          setError("");
        }}
        aria-pressed={selected}
        aria-label={`${name}: ${option.value}`}
      >
        <span className="trial-access-radio" aria-hidden="true" />
        <span className="trial-access-option-copy">
          <strong>{option.value}</strong>
          <span>{option.detail}</span>
        </span>
      </button>
    );
  };

  const renderIssue = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Trial Access Help</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="trial-access-intro">Choose what you need help with.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="trial-access-section">
          <Form.Label>What do you need?</Form.Label>
          <div className="trial-access-options" aria-label="Trial issue">
            {TRIAL_ISSUES.map((option) =>
              renderOption(option, trialIssue, setTrialIssue, "trial-access-option", "Trial issue")
            )}
          </div>
        </Form.Group>

        <Form.Group className="trial-access-section" controlId="teacherTrialAccessNote">
          <Form.Label>Short note</Form.Label>
          <Form.Text as="div" className="trial-access-field-label">
            What happened?
          </Form.Text>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setError("");
            }}
            aria-label="What happened?"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="trial-access-footer-note">
          {trialIssue
            ? wantsPurchaseFromIssue
              ? "Purchase details selected."
              : wantsExtendFromIssue
                ? "Extend trial selected."
              : "Trial issue selected."
            : "Choose a trial issue first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleNext} disabled={!canReview}>
          {wantsPurchaseFromIssue
            ? "Continue to purchase details"
            : wantsExtendFromIssue
              ? "Continue to extend trial"
              : isSubmitting
                ? "Sending..."
                : "Send request"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Trial Review Sent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="trial-access-intro">
          {isDuplicatePending
            ? `A trial review request is already in progress for ${displayedSchoolName}.`
            : `The trial review request has been sent for ${displayedSchoolName}.`}
        </p>
        <Alert variant="success" className="trial-access-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {isDuplicatePending
                ? "Your trial review request is already in progress."
                : "Your trial review request has been sent."}
            </strong>
            <span>
              {supportReference ? `Reference: ${supportReference}. ` : ""}
              ALPACA trial access has not been changed automatically.
            </span>
          </span>
        </Alert>
        <div className="trial-access-next">
          <strong>What next?</strong>
          <span>
            {wantsPurchaseDetails
              ? embedded
                ? "Continue to purchase details for next steps, or close this workflow and return to SENSAI."
                : "Continue to purchase details for next steps, or return to Teacher Dashboard."
              : embedded
                ? "Close this workflow and return to SENSAI while ALPACA reviews the request."
                : "Return to the Help Centre while ALPACA reviews the request."}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer className="trial-access-success-footer">
        <span className="trial-access-footer-note">Task complete.</span>
        {wantsPurchaseDetails && (
          <Button variant="outline-secondary" onClick={openPurchaseDetails}>
            Continue to purchase details
          </Button>
        )}
        {!embedded && (
          <Button variant="outline-secondary" onClick={returnToTeacherDashboard}>
            Return to Help Centre
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Close
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-trial-access-modal"
    >
      {stage === "issue" && renderIssue()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherTrialAccessStatusModal;
